import { between, integer } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import { requiredWithMessage } from '@/helpers/CustomValidator';
import Form from './Form.vue';

export interface ABTestData {
  outputs_number_test: number;
  initial_number_outputs?: number | null;
  outputs_to_delete?: string[] | null;
}

export const createDefaultData = (overrides: Partial<ABTestData> = {}): ABTestData => ({
  outputs_number_test: 2,
  ...overrides,
});

export const ABTest: FlowOperatorMetadata<ABTestData> = {
  id: 'boxabtest',
  kind: 'action',
  label: 'flows.operators.abTest.label',
  icon: 'fa-code-branch',
  hasConfiguration: true,

  availableIn: [TypeCampaignEnum.AUTOMATION],

  incompatibilities: {
    automation: {
      nextOperators: [
        'boxabandonpanier',
        'boxperiodiclaunching',
        'boxstatuscommand',
        'registeronsite',
        'subscribetonewsletter',
      ],
    },
    bulk: {
      nextOperators: [
        'boxabandonpanier',
        'boxperiodiclaunching',
        'boxstatuscommand',
        'registeronsite',
        'subscribetonewsletter',
        'boxchoosebulklist',
        'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs(data: ABTestData): number {
    return data.outputs_number_test ?? 2;
  },

  beforeInsert(data: ABTestData): ABTestData {
    const cleanData = { ...data };
    if ('initial_number_outputs' in cleanData) {
      delete cleanData.initial_number_outputs;
    }
    if ('outputs_to_delete' in cleanData) {
      delete cleanData.outputs_to_delete;
    }
    return cleanData;
  },

  async validate(data: ABTestData): Promise<ValidationResult> {
    const rules = {
      outputs_number_test: {
        requiredWithMessage: requiredWithMessage(),
        integer,
        between: between(2, 6),
      },
    };

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },
};

export default ABTest;
